import React, { Suspense } from "react";
import { Box, Typography, useTheme, alpha, Skeleton } from "@mui/material";
import { ChartContainer } from "./ChartContainer";
import { EmptyState } from "../EmptyState/EmptyState";
import { theme } from "../../../../theme";

// Lazy load the chart library to improve initial load time
const ReactApexChart = React.lazy(() => import("react-apexcharts"));

export interface BarChartProps {
  title: string;
  icon?: string;
  data: Array<{
    name: string;
    value: number;
  }>;
  height?: number;
  subtitle?: string;
  action?: React.ReactNode;
  isLoading?: boolean;
  emptyStateMessage?: string;
  formatValue?: (value: number) => string;
  delay?: number;
  color?: string;
  maxItems?: number;
  valuePrefix?: string;
  valueSuffix?: string;
}

export const BarChart: React.FC<BarChartProps> = ({
  title,
  icon = "bar_chart",
  data,
  height = 350,
  subtitle,
  action,
  isLoading = false,
  emptyStateMessage = "No data available",
  formatValue = (value: number) => value.toString(),
  delay = 0,
  color,
  maxItems = 10,
  valuePrefix = "",
  valueSuffix = "",
}) => {
  const theme = useTheme();
  const barColor = color || theme.palette.primary.main;

  // Sort data by value in descending order and take top N items
  const sortedData = [...data]
    .sort((a, b) => b.value - a.value)
    .slice(0, maxItems);

  // Chart options
  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        barHeight: "70%",
        distributed: true,
        dataLabels: {
          position: "center",
        },
      },
    },
    colors: Array(sortedData.length).fill(alpha(barColor, 0.85)),
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: {
        colors: ["#fff"],
        fontSize: "12px",
        fontFamily: theme.typography.fontFamily,
        fontWeight: 500,
      },
      formatter: function (value: number) {
        return valuePrefix + formatValue(value) + valueSuffix;
      },
      offsetX: 0,
    },
    legend: {
      show: false,
    },
    grid: {
      show: true,
      borderColor: alpha(theme.palette.divider, 0.05),
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 0,
        right: 15,
        bottom: 0,
        left: 15,
      },
    },
    xaxis: {
      categories: sortedData.map((item) => item.name),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        style: {
          colors: theme.palette.text.secondary,
          fontSize: "12px",
          fontFamily: theme.typography.fontFamily,
        },
        formatter: (value: number) => {
          if (typeof value === "number") {
            const roundedValue = Math.round(value);
            const formattedValue =
              valuePrefix + formatValue(roundedValue) + valueSuffix;
            return formattedValue.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
          }
          return value;
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.primary,
          fontSize: "12px",
          fontFamily: theme.typography.fontFamily,
          fontWeight: 500,
        },
        maxWidth: 150,
        trim: true,
        offsetX: -10,
      },
    },
    tooltip: {
      enabled: true,
      theme: theme.palette.mode,
      y: {
        title: {
          formatter: () => "",
        },
        formatter: (value: number) =>
          valuePrefix + formatValue(value) + valueSuffix,
      },
      marker: {
        show: false,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];
        const name = w.globals.labels[dataPointIndex];
        return `
          <div class="apexcharts-tooltip-box" style="
            padding: 8px 12px;
            background: ${alpha(barColor, 0.9)};
            color: white;
            border-radius: 4px;
            font-family: ${theme.typography.fontFamily};
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            text-align: center;
          ">
            <div style="font-weight: 500; margin-bottom: 4px;">${name}</div>
            <div style="font-size: 14px;">${
              valuePrefix + formatValue(value) + valueSuffix
            }</div>
          </div>
        `;
      },
    },
    states: {
      hover: {
        filter: {
          type: "darken",
          value: 0.1,
        },
      },
      active: {
        filter: {
          type: "darken",
          value: 0.15,
        },
      },
    },
  };

  // Prepare series data
  const series = [
    {
      name: title,
      data: sortedData.map((item) => item.value),
    },
  ];

  // Chart with suspense for loading state
  const ChartWithSuspense = () => (
    <Suspense
      fallback={
        <Box sx={{ height, width: "100%" }}>
          <Skeleton
            variant="rectangular"
            height={height}
            sx={{
              borderRadius: 2,
              transform: "none",
            }}
          />
        </Box>
      }
    >
      <ReactApexChart
        options={chartOptions}
        series={series}
        type="bar"
        height={height}
      />
    </Suspense>
  );

  // Empty state component
  const EmptyStateComponent = (
    <EmptyState
      icon="bar_chart"
      title="No Data Available"
      message={emptyStateMessage}
      height={height}
    />
  );

  return (
    <ChartContainer
      title={title}
      icon={icon}
      subtitle={subtitle}
      action={action}
      isLoading={false}
      emptyState={sortedData.length === 0 ? EmptyStateComponent : undefined}
      delay={delay}
    >
      {isLoading ? (
        <Box sx={{ height, width: "100%" }}>
          <Skeleton
            variant="rectangular"
            height={height}
            sx={{
              borderRadius: 2,
              transform: "none",
            }}
          />
        </Box>
      ) : (
        <ChartWithSuspense />
      )}
    </ChartContainer>
  );
};
